import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, Phone, Mail, MapPin, Calendar, Plus, Search, Filter,
  MoreVertical, Clock, CheckCircle, AlertTriangle, TrendingUp,
  UserPlus, Target, Zap, MessageSquare, PhoneCall, FileText,
  ArrowRight, Star, Flame, Snowflake, RefreshCw, ChevronDown,
  Building2, Globe, Share2, X, Edit, Trash2, Send, CalendarDays,
  ClipboardList, Bell, BarChart3, PieChart, Activity, Download
} from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const centerApi = axios.create({ baseURL: API });
centerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('center_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Pipeline stages config
const PIPELINE_STAGES = [
  { id: 'new', label: 'New Lead', color: 'bg-blue-500', icon: UserPlus },
  { id: 'contacted', label: 'Contacted', color: 'bg-purple-500', icon: Phone },
  { id: 'follow-up', label: 'Follow-up', color: 'bg-amber-500', icon: Clock },
  { id: 'interested', label: 'Interested', color: 'bg-emerald-500', icon: Target },
  { id: 'not-interested', label: 'Not Interested', color: 'bg-slate-400', icon: X },
  { id: 'converted', label: 'Converted', color: 'bg-green-600', icon: CheckCircle }
];

const LEAD_SOURCES = [
  { id: 'walk-in', label: 'Walk-in', icon: Building2 },
  { id: 'call', label: 'Phone Call', icon: Phone },
  { id: 'website', label: 'Website', icon: Globe },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { id: 'campaign', label: 'Campaign', icon: Target },
  { id: 'referral', label: 'Referral', icon: Share2 }
];

const CRMSystem = ({ courses = [] }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterType, setFilterType] = useState('');
  const [activeView, setActiveView] = useState('kanban'); // kanban, list
  
  // Dialog states
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [addFollowupOpen, setAddFollowupOpen] = useState(false);
  const [logCallOpen, setLogCallOpen] = useState(false);
  
  // Form states
  const [leadForm, setLeadForm] = useState({
    name: '', mobile: '', email: '', course_interested: '',
    preferred_mode: 'center', source: 'walk-in', city: '', location: '', remarks: ''
  });
  const [taskForm, setTaskForm] = useState({
    task_type: 'call', title: '', description: '', due_date: '', priority: 'medium'
  });
  const [noteText, setNoteText] = useState('');
  const [followupDate, setFollowupDate] = useState('');
  const [followupNotes, setFollowupNotes] = useState('');
  const [callOutcome, setCallOutcome] = useState('answered');
  const [callNotes, setCallNotes] = useState('');
  
  // Drag state
  const [draggedLead, setDraggedLead] = useState(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, statsRes, tasksRes] = await Promise.all([
        centerApi.get('/center/leads'),
        centerApi.get('/center/leads/stats'),
        centerApi.get('/center/tasks')
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data);
      setTasks(tasksRes.data || []);
    } catch (error) {
      console.error('Error fetching CRM data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchSearch = !searchTerm || 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.mobile?.includes(searchTerm) ||
      lead.lead_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchSource = !filterSource || lead.source === filterSource;
    const matchType = !filterType || lead.lead_type === filterType;
    return matchSearch && matchSource && matchType;
  });

  // Get leads by stage
  const getLeadsByStage = (stage) => filteredLeads.filter(l => l.stage === stage);

  // Download Leads (Excel) — respects current search/source/type filters.
  const downloadLeadsExcel = () => {
    if (!filteredLeads.length) {
      toast.error('Koi lead nahi mila download karne ke liye');
      return;
    }
    const stageLabel = (id) => PIPELINE_STAGES.find(s => s.id === id)?.label || id || '';
    const rows = filteredLeads.map((l, idx) => ({
      'Sr No': idx + 1,
      'Name': l.name || '',
      'Course': l.course_interested || l.course || '',
      'Contact No': l.mobile || l.phone || '',
      'Date': l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      }) : '',
      'Stage': stageLabel(l.stage),
      'Source': l.source || '',
      'Lead Type': l.lead_type || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 6 }, { wch: 22 }, { wch: 24 }, { wch: 14 }, { wch: 14 },
      { wch: 12 }, { wch: 14 }, { wch: 10 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Leads_${today}.xlsx`);
    toast.success(`${rows.length} leads downloaded`);
  };

  // Add Lead
  const handleAddLead = async () => {
    if (!leadForm.name || !leadForm.mobile || !leadForm.course_interested) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await centerApi.post('/center/leads', leadForm);
      toast.success('Lead added successfully!');
      setAddLeadOpen(false);
      setLeadForm({
        name: '', mobile: '', email: '', course_interested: '',
        preferred_mode: 'center', source: 'walk-in', city: '', location: '', remarks: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lead');
    }
  };

  // Update Stage (Drag & Drop)
  const handleDrop = async (leadId, newStage) => {
    try {
      await centerApi.put(`/center/leads/${leadId}/stage?stage=${newStage}`);
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, stage: newStage } : l));
      toast.success(`Lead moved to ${PIPELINE_STAGES.find(s => s.id === newStage)?.label}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update lead');
    }
    setDraggedLead(null);
  };

  // Open Lead Detail
  const openLeadDetail = async (lead) => {
    try {
      const res = await centerApi.get(`/center/leads/${lead.id}`);
      setSelectedLead(res.data);
      setLeadDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load lead details');
    }
  };

  // Add Note
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await centerApi.post(`/center/leads/${selectedLead.id}/note?note=${encodeURIComponent(noteText)}`);
      toast.success('Note added');
      setNoteText('');
      setAddNoteOpen(false);
      openLeadDetail(selectedLead);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  // Add Follow-up
  const handleAddFollowup = async () => {
    if (!followupDate) return;
    try {
      await centerApi.post(`/center/leads/${selectedLead.id}/followup`, {
        lead_id: selectedLead.id,
        followup_date: followupDate,
        notes: followupNotes
      });
      toast.success('Follow-up scheduled');
      setFollowupDate('');
      setFollowupNotes('');
      setAddFollowupOpen(false);
      openLeadDetail(selectedLead);
      fetchData();
    } catch (error) {
      toast.error('Failed to schedule follow-up');
    }
  };

  // Log Call
  const handleLogCall = async () => {
    try {
      await centerApi.post(`/center/leads/${selectedLead.id}/call?outcome=${callOutcome}&notes=${encodeURIComponent(callNotes)}`);
      toast.success('Call logged');
      setCallOutcome('answered');
      setCallNotes('');
      setLogCallOpen(false);
      openLeadDetail(selectedLead);
    } catch (error) {
      toast.error('Failed to log call');
    }
  };

  // Add Task
  const handleAddTask = async () => {
    if (!taskForm.title) return;
    try {
      await centerApi.post('/center/tasks', {
        ...taskForm,
        lead_id: selectedLead.id
      });
      toast.success('Task created');
      setTaskForm({ task_type: 'call', title: '', description: '', due_date: '', priority: 'medium' });
      setAddTaskOpen(false);
      openLeadDetail(selectedLead);
      fetchData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  // Complete Task
  const handleCompleteTask = async (taskId) => {
    try {
      await centerApi.put(`/center/tasks/${taskId}?status=completed`);
      toast.success('Task completed');
      fetchData();
      if (selectedLead) openLeadDetail(selectedLead);
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  // Convert Lead
  const handleConvertLead = async () => {
    try {
      const res = await centerApi.post(`/center/leads/${selectedLead.id}/convert`);
      toast.success('Lead ready for admission! Fill in course details to complete.');
      // Return conversion data for parent to use
      return res.data.student_data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    }
  };

  // Lead Type Badge
  const LeadTypeBadge = ({ type }) => {
    const config = {
      hot: { icon: Flame, color: 'text-red-500 bg-red-50', label: 'Hot' },
      warm: { icon: Star, color: 'text-amber-500 bg-amber-50', label: 'Warm' },
      cold: { icon: Snowflake, color: 'text-blue-400 bg-blue-50', label: 'Cold' }
    };
    const { icon: Icon, color, label } = config[type] || config.warm;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
        <Icon className="w-3 h-3" /> {label}
      </span>
    );
  };

  // Lead Card Component
  const LeadCard = ({ lead }) => (
    <div
      draggable
      onDragStart={() => setDraggedLead(lead)}
      onDragEnd={() => setDraggedLead(null)}
      onClick={() => openLeadDetail(lead)}
      className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer group"
    >
      <div className="flex justify-between items-start mb-2">
        <div>
          <p className="font-semibold text-slate-800">{lead.name}</p>
          <p className="text-xs text-slate-400">{lead.lead_id}</p>
        </div>
        <LeadTypeBadge type={lead.lead_type} />
      </div>
      
      <div className="space-y-1 text-sm text-slate-600">
        <p className="flex items-center gap-2">
          <Phone className="w-3 h-3" /> {lead.mobile}
        </p>
        <p className="flex items-center gap-2">
          <Target className="w-3 h-3" /> {lead.course_interested}
        </p>
        {lead.next_followup_date && (
          <p className="flex items-center gap-2 text-amber-600">
            <Calendar className="w-3 h-3" /> Follow-up: {lead.next_followup_date}
          </p>
        )}
      </div>
      
      <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center">
        <span className="text-xs text-slate-400 capitalize flex items-center gap-1">
          {LEAD_SOURCES.find(s => s.id === lead.source)?.icon && 
            React.createElement(LEAD_SOURCES.find(s => s.id === lead.source).icon, { className: "w-3 h-3" })}
          {lead.source}
        </span>
        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">Score: {lead.score}</span>
      </div>
    </div>
  );

  // Pipeline Column
  const PipelineColumn = ({ stage }) => {
    const stageConfig = PIPELINE_STAGES.find(s => s.id === stage.id);
    const stageLeads = getLeadsByStage(stage.id);
    const Icon = stageConfig.icon;
    
    return (
      <div
        className="flex-shrink-0 w-72 bg-slate-50 rounded-2xl p-4"
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => draggedLead && handleDrop(draggedLead.id, stage.id)}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg ${stageConfig.color} flex items-center justify-center`}>
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{stageConfig.label}</h3>
              <p className="text-xs text-slate-500">{stageLeads.length} leads</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-3 max-h-[calc(100vh-380px)] overflow-y-auto">
          {stageLeads.map(lead => (
            <LeadCard key={lead.id} lead={lead} />
          ))}
          {stageLeads.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">
              No leads
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CRM Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between">
            <Users className="w-8 h-8 opacity-80" />
            <span className="text-2xl font-bold">{stats?.total_leads || 0}</span>
          </div>
          <p className="text-sm opacity-80 mt-2">Total Leads</p>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between">
            <UserPlus className="w-8 h-8 opacity-80" />
            <span className="text-2xl font-bold">{stats?.new_today || 0}</span>
          </div>
          <p className="text-sm opacity-80 mt-2">New Today</p>
        </div>
        
        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between">
            <Calendar className="w-8 h-8 opacity-80" />
            <span className="text-2xl font-bold">{stats?.followups_today || 0}</span>
          </div>
          <p className="text-sm opacity-80 mt-2">Follow-ups Today</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between">
            <Flame className="w-8 h-8 opacity-80" />
            <span className="text-2xl font-bold">{stats?.hot_leads || 0}</span>
          </div>
          <p className="text-sm opacity-80 mt-2">Hot Leads</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between">
            <CheckCircle className="w-8 h-8 opacity-80" />
            <span className="text-2xl font-bold">{stats?.conversions_month || 0}</span>
          </div>
          <p className="text-sm opacity-80 mt-2">Conversions</p>
        </div>
        
        <div className="bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between">
            <TrendingUp className="w-8 h-8 opacity-80" />
            <span className="text-2xl font-bold">{stats?.conversion_rate || 0}%</span>
          </div>
          <p className="text-sm opacity-80 mt-2">Conversion Rate</p>
        </div>
      </div>

      {/* Alerts */}
      {(stats?.overdue_followups > 0 || stats?.pending_tasks > 0) && (
        <div className="flex flex-wrap gap-3">
          {stats?.overdue_followups > 0 && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-xl">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">{stats.overdue_followups} Overdue Follow-ups</span>
            </div>
          )}
          {stats?.pending_tasks > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-xl">
              <ClipboardList className="w-4 h-4" />
              <span className="text-sm font-medium">{stats.pending_tasks} Pending Tasks</span>
            </div>
          )}
        </div>
      )}

      {/* CRM Internal Tabs */}
      <Tabs defaultValue="pipeline" className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl mb-4">
          <TabsTrigger value="pipeline" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Target className="w-4 h-4 mr-2" /> Pipeline
          </TabsTrigger>
          <TabsTrigger value="tasks" className="rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <ClipboardList className="w-4 h-4 mr-2" /> Tasks ({tasks.filter(t => t.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4 mr-2" /> Analytics
          </TabsTrigger>
        </TabsList>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline">
          {/* Actions Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 bg-white rounded-2xl p-4 border border-slate-200 mb-4">
            <div className="flex items-center gap-3">
              <Button onClick={() => setAddLeadOpen(true)} className="bg-blue-600 hover:bg-blue-700 rounded-xl" data-testid="crm-add-lead-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Lead
              </Button>
              <Button
                onClick={downloadLeadsExcel}
                variant="outline"
                className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="crm-download-leads-btn"
                title="Download filtered leads as Excel"
              >
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 rounded-xl"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="h-10 px-3 border rounded-xl bg-white text-sm"
              >
                <option value="">All Sources</option>
                {LEAD_SOURCES.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
              
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="h-10 px-3 border rounded-xl bg-white text-sm"
              >
                <option value="">All Types</option>
                <option value="hot">🔥 Hot</option>
                <option value="warm">⭐ Warm</option>
                <option value="cold">❄️ Cold</option>
              </select>
              
              <Button variant="outline" onClick={fetchData} className="rounded-xl">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Kanban Board */}
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {PIPELINE_STAGES.map(stage => (
                <PipelineColumn key={stage.id} stage={stage} />
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Tasks */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" /> Pending Tasks
              </h3>
              <div className="space-y-3">
                {tasks.filter(t => t.status === 'pending').length > 0 ? (
                  tasks.filter(t => t.status === 'pending').map(task => (
                    <div key={task.id} className="bg-slate-50 rounded-xl p-4 flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            task.priority === 'high' ? 'bg-red-100 text-red-700' :
                            task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {task.priority}
                          </span>
                          <span className="text-xs text-slate-400 capitalize">{task.task_type?.replace('_', ' ')}</span>
                        </div>
                        <p className="font-medium text-slate-800">{task.title}</p>
                        {task.description && <p className="text-sm text-slate-500 mt-1">{task.description}</p>}
                        {task.due_date && (
                          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Due: {task.due_date}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCompleteTask(task.id)}
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No pending tasks</p>
                  </div>
                )}
              </div>
            </div>

            {/* Completed Tasks */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" /> Completed Tasks
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {tasks.filter(t => t.status === 'completed').length > 0 ? (
                  tasks.filter(t => t.status === 'completed').map(task => (
                    <div key={task.id} className="bg-emerald-50 rounded-xl p-4 opacity-70">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-slate-400 capitalize">{task.task_type?.replace('_', ' ')}</span>
                      </div>
                      <p className="font-medium text-slate-700 line-through">{task.title}</p>
                      {task.completed_at && (
                        <p className="text-xs text-slate-400 mt-1">
                          Completed: {new Date(task.completed_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <p>No completed tasks yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Funnel Chart */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" /> Lead Funnel
              </h3>
              <div className="space-y-3">
                {PIPELINE_STAGES.map((stage, idx) => {
                  const count = stats?.pipeline_counts?.[stage.id] || 0;
                  const maxCount = Math.max(...Object.values(stats?.pipeline_counts || {1:1}));
                  const width = maxCount > 0 ? (count / maxCount * 100) : 0;
                  return (
                    <div key={stage.id} className="flex items-center gap-3">
                      <div className="w-28 text-sm text-slate-600 flex items-center gap-2">
                        {React.createElement(stage.icon, { className: "w-4 h-4" })}
                        {stage.label}
                      </div>
                      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                        <div 
                          className={`h-full ${stage.color} rounded-full flex items-center justify-end pr-2 transition-all duration-500`}
                          style={{ width: `${Math.max(width, count > 0 ? 15 : 0)}%` }}
                        >
                          <span className="text-white text-xs font-medium">{count}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Source Distribution */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-500" /> Lead Sources
              </h3>
              <div className="space-y-3">
                {stats?.source_stats?.length > 0 ? (
                  stats.source_stats.map(s => {
                    const sourceConfig = LEAD_SOURCES.find(ls => ls.id === s.source);
                    const Icon = sourceConfig?.icon || Globe;
                    const percentage = stats.total_leads > 0 ? Math.round(s.count / stats.total_leads * 100) : 0;
                    return (
                      <div key={s.source} className="flex items-center gap-3">
                        <div className="w-28 text-sm text-slate-600 flex items-center gap-2 capitalize">
                          <Icon className="w-4 h-4" />
                          {s.source}
                        </div>
                        <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-end pr-2"
                            style={{ width: `${percentage}%` }}
                          >
                            <span className="text-white text-xs font-medium">{s.count}</span>
                          </div>
                        </div>
                        <span className="text-sm text-slate-500 w-12 text-right">{percentage}%</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <p>No source data yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Lead Type Distribution */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Flame className="w-5 h-5 text-red-500" /> Lead Quality
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <Flame className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-600">{stats?.hot_leads || 0}</p>
                  <p className="text-sm text-red-500">Hot Leads</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <Star className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-amber-600">{stats?.warm_leads || 0}</p>
                  <p className="text-sm text-amber-500">Warm Leads</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <Snowflake className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-500">{stats?.cold_leads || 0}</p>
                  <p className="text-sm text-blue-400">Cold Leads</p>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-500" /> Performance
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                  <span className="text-slate-700">Conversion Rate</span>
                  <span className="text-2xl font-bold text-emerald-600">{stats?.conversion_rate || 0}%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                  <span className="text-slate-700">This Month Conversions</span>
                  <span className="text-2xl font-bold text-purple-600">{stats?.conversions_month || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                  <span className="text-slate-700">Pending Follow-ups</span>
                  <span className="text-2xl font-bold text-amber-600">{stats?.followups_today || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                  <span className="text-slate-700">Overdue Follow-ups</span>
                  <span className="text-2xl font-bold text-red-600">{stats?.overdue_followups || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Lead Dialog */}
      <Dialog open={addLeadOpen} onOpenChange={setAddLeadOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Add New Lead
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Student Name *</Label>
              <Input
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                placeholder="Full name"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Mobile Number *</Label>
              <Input
                value={leadForm.mobile}
                onChange={(e) => setLeadForm({ ...leadForm, mobile: e.target.value })}
                placeholder="10-digit mobile"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={leadForm.email}
                onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                placeholder="email@example.com"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Course Interested *</Label>
              <select
                value={leadForm.course_interested}
                onChange={(e) => setLeadForm({ ...leadForm, course_interested: e.target.value })}
                className="w-full h-10 px-3 border rounded-xl bg-white"
              >
                <option value="">Select Course</option>
                {courses.map(c => (
                  <option key={c.id || c.name} value={c.name}>{c.name}</option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Preferred Mode</Label>
              <select
                value={leadForm.preferred_mode}
                onChange={(e) => setLeadForm({ ...leadForm, preferred_mode: e.target.value })}
                className="w-full h-10 px-3 border rounded-xl bg-white"
              >
                <option value="center">At Center</option>
                <option value="online">Online</option>
              </select>
            </div>
            <div>
              <Label>Source</Label>
              <select
                value={leadForm.source}
                onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })}
                className="w-full h-10 px-3 border rounded-xl bg-white"
              >
                {LEAD_SOURCES.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>City</Label>
              <Input
                value={leadForm.city}
                onChange={(e) => setLeadForm({ ...leadForm, city: e.target.value })}
                placeholder="City name"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={leadForm.location}
                onChange={(e) => setLeadForm({ ...leadForm, location: e.target.value })}
                placeholder="Area/Locality"
                className="rounded-xl"
              />
            </div>
            <div className="col-span-2">
              <Label>Remarks</Label>
              <textarea
                value={leadForm.remarks}
                onChange={(e) => setLeadForm({ ...leadForm, remarks: e.target.value })}
                placeholder="Additional notes..."
                className="w-full h-20 px-3 py-2 border rounded-xl resize-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setAddLeadOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleAddLead} className="bg-blue-600 hover:bg-blue-700 rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> Add Lead
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={leadDetailOpen} onOpenChange={setLeadDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-lg font-bold">
                      {selectedLead.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-xl font-bold">{selectedLead.name}</p>
                      <p className="text-sm text-slate-500">{selectedLead.lead_id}</p>
                    </div>
                  </div>
                  <LeadTypeBadge type={selectedLead.lead_type} />
                </DialogTitle>
              </DialogHeader>
              
              <div className="grid grid-cols-3 gap-6 mt-6">
                {/* Left Column - Info */}
                <div className="col-span-2 space-y-6">
                  {/* Contact Info */}
                  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                    <h4 className="font-semibold text-slate-800">Contact Information</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <a href={`tel:${selectedLead.mobile}`} className="text-blue-600 hover:underline">{selectedLead.mobile}</a>
                      </p>
                      {selectedLead.email && (
                        <p className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <a href={`mailto:${selectedLead.email}`} className="text-blue-600 hover:underline">{selectedLead.email}</a>
                        </p>
                      )}
                      {selectedLead.city && (
                        <p className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          {selectedLead.city} {selectedLead.location && `- ${selectedLead.location}`}
                        </p>
                      )}
                      <p className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-slate-400" />
                        {selectedLead.course_interested}
                      </p>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setLogCallOpen(true)} className="rounded-lg">
                      <PhoneCall className="w-4 h-4 mr-1" /> Log Call
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAddNoteOpen(true)} className="rounded-lg">
                      <FileText className="w-4 h-4 mr-1" /> Add Note
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAddFollowupOpen(true)} className="rounded-lg">
                      <Calendar className="w-4 h-4 mr-1" /> Schedule Follow-up
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAddTaskOpen(true)} className="rounded-lg">
                      <ClipboardList className="w-4 h-4 mr-1" /> Add Task
                    </Button>
                    {selectedLead.stage !== 'converted' && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg" onClick={handleConvertLead}>
                        <CheckCircle className="w-4 h-4 mr-1" /> Convert
                      </Button>
                    )}
                  </div>
                  
                  {/* Timeline */}
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-4">Activity Timeline</h4>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {selectedLead.activities?.length > 0 ? (
                        selectedLead.activities.map((activity, idx) => (
                          <div key={idx} className="flex gap-3 text-sm">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              activity.activity_type === 'call' ? 'bg-green-100 text-green-600' :
                              activity.activity_type === 'note' ? 'bg-blue-100 text-blue-600' :
                              activity.activity_type === 'stage_change' ? 'bg-purple-100 text-purple-600' :
                              activity.activity_type === 'followup' ? 'bg-amber-100 text-amber-600' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {activity.activity_type === 'call' ? <PhoneCall className="w-4 h-4" /> :
                               activity.activity_type === 'note' ? <FileText className="w-4 h-4" /> :
                               activity.activity_type === 'stage_change' ? <ArrowRight className="w-4 h-4" /> :
                               activity.activity_type === 'followup' ? <Calendar className="w-4 h-4" /> :
                               <Activity className="w-4 h-4" />}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-slate-800">{activity.title}</p>
                              {activity.description && <p className="text-slate-600">{activity.description}</p>}
                              {activity.old_value && activity.new_value && (
                                <p className="text-slate-500">
                                  {activity.old_value} → {activity.new_value}
                                </p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(activity.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-400 text-center py-4">No activities yet</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Right Column - Status & Tasks */}
                <div className="space-y-6">
                  {/* Status Card */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="font-semibold text-slate-800 mb-3">Lead Status</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Stage</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          PIPELINE_STAGES.find(s => s.id === selectedLead.stage)?.color
                        } text-white`}>
                          {PIPELINE_STAGES.find(s => s.id === selectedLead.stage)?.label}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Score</span>
                        <span className="font-semibold">{selectedLead.score}/100</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Source</span>
                        <span className="capitalize">{selectedLead.source}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Follow-ups</span>
                        <span>{selectedLead.followup_count || 0}</span>
                      </div>
                      {selectedLead.next_followup_date && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Next Follow-up</span>
                          <span className="text-amber-600 font-medium">{selectedLead.next_followup_date}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Tasks */}
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-3">Tasks</h4>
                    <div className="space-y-2">
                      {selectedLead.tasks?.filter(t => t.status === 'pending').map(task => (
                        <div key={task.id} className="bg-white border rounded-lg p-3 text-sm">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{task.title}</p>
                              {task.due_date && (
                                <p className="text-xs text-slate-500 mt-1">Due: {task.due_date}</p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCompleteTask(task.id)}
                              className="h-7 w-7 p-0"
                            >
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {(!selectedLead.tasks || selectedLead.tasks.filter(t => t.status === 'pending').length === 0) && (
                        <p className="text-slate-400 text-sm text-center py-2">No pending tasks</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={addNoteOpen} onOpenChange={setAddNoteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter your note..."
            className="w-full h-32 p-3 border rounded-xl resize-none"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAddNoteOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleAddNote} className="bg-blue-600 hover:bg-blue-700 rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> Add Note
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Follow-up Dialog */}
      <Dialog open={addFollowupOpen} onOpenChange={setAddFollowupOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Follow-up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Follow-up Date *</Label>
              <Input
                type="date"
                value={followupDate}
                onChange={(e) => setFollowupDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <textarea
                value={followupNotes}
                onChange={(e) => setFollowupNotes(e.target.value)}
                placeholder="Follow-up notes..."
                className="w-full h-20 p-3 border rounded-xl resize-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setAddFollowupOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleAddFollowup} className="bg-amber-600 hover:bg-amber-700 rounded-xl">
              <Calendar className="w-4 h-4 mr-2" /> Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Log Call Dialog */}
      <Dialog open={logCallOpen} onOpenChange={setLogCallOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Call</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Call Outcome</Label>
              <select
                value={callOutcome}
                onChange={(e) => setCallOutcome(e.target.value)}
                className="w-full h-10 px-3 border rounded-xl bg-white"
              >
                <option value="answered">Answered</option>
                <option value="no-answer">No Answer</option>
                <option value="busy">Busy</option>
                <option value="callback">Callback Requested</option>
                <option value="wrong-number">Wrong Number</option>
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <textarea
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                placeholder="Call notes..."
                className="w-full h-20 p-3 border rounded-xl resize-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setLogCallOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleLogCall} className="bg-green-600 hover:bg-green-700 rounded-xl">
              <PhoneCall className="w-4 h-4 mr-2" /> Log Call
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={addTaskOpen} onOpenChange={setAddTaskOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Task Type</Label>
              <select
                value={taskForm.task_type}
                onChange={(e) => setTaskForm({ ...taskForm, task_type: e.target.value })}
                className="w-full h-10 px-3 border rounded-xl bg-white"
              >
                <option value="call">Call Student</option>
                <option value="send_details">Send Details</option>
                <option value="schedule_visit">Schedule Visit</option>
                <option value="follow_up">Follow Up</option>
                <option value="demo">Arrange Demo</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="Task title"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Priority</Label>
              <select
                value={taskForm.priority}
                onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                className="w-full h-10 px-3 border rounded-xl bg-white"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Task description..."
                className="w-full h-20 p-3 border rounded-xl resize-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setAddTaskOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleAddTask} className="bg-purple-600 hover:bg-purple-700 rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> Add Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMSystem;
